import type { JsonTimesheetAddressModel } from '../../models/complex/timesheet-address.model';
import { AddressState, TimesheetAddressState } from './address-collection.state';
import { outboundAddrNumber } from './address-number';

/**
 * Indexes the address collection by id, for the several places downstream of the slice that only
 * hold a timesheet entry's address id and need the address it points at.
 *
 * @param addresses the address collection to index
 * @returns the addresses keyed by {@link AddressState.id}
 */
export function addressesById(addresses: readonly AddressState[]): Map<string, AddressState> {
    return new Map(addresses.map((address) => [address.id, address]));
}

/**
 * Projects an address down to the fields a timesheet entry's address carries.
 *
 * This is the shape `mapAddress` (the submit out-model builder) and the `addressRequired` schema
 * rule read, so resolving a stored id through this rather than handing over the whole `AddressState`
 * keeps both from reaching into fields — the address book's contact and naming data — that are none
 * of the timesheet's business.
 *
 * @param address the address to project
 * @returns the timesheet-shaped address
 */
export function toTimesheetAddress(address: AddressState): TimesheetAddressState {
    const { street, city, postCode, country, addrText, addrNumber, stateOfUSA, addrKey, id } = address;
    return {
        street,
        city,
        postCode,
        country,
        addrText,
        addrNumber,
        ...(stateOfUSA === undefined ? {} : { stateOfUSA }),
        ...(addrKey === undefined ? {} : { addrKey }),
        // the entry's own copy is never offered for hiding; that is an address-book affordance
        allowToHide: false,
        id,
    };
}

/**
 * Projects an address into the legacy `TimesheetAddressModel` JSON shape.
 *
 * The result is `TimesheetAddressModel`'s own JSON, not `TimesheetAddressState`: the two disagree
 * on the US-state key (`statesOfUSA` against `stateOfUSA`) and on `country`, which a timesheet
 * address always carries as the user's own rather than the address's — `TimesheetAddressModel`'s
 * constructor overwrites it from user master. Rehydration reads this back through
 * `TimesheetAddressModelFromJson`, so emitting the state shape here would silently drop the US
 * state and the address's real country.
 *
 * @param address the address to project
 * @param userCountry the country every timesheet address carries
 * @returns the legacy JSON address shape
 */
export function toJsonTimesheetAddress(address: AddressState, userCountry: string): JsonTimesheetAddressModel {
    return {
        street: address.street,
        city: address.city,
        postCode: address.postCode,
        country: userCountry,
        addrText: address.addrText,
        addrNumber: outboundAddrNumber(address.addrNumber),
        statesOfUSA: address.stateOfUSA ?? '',
        addrKey: address.addrKey ?? '',
    };
}
